import { useEffect, useMemo } from 'react';
import { I18nextProvider, initReactI18next, useTranslation } from 'react-i18next';
import { useLoaderData, useLocation, useRouteError, useRouteLoaderData } from 'react-router';
import FingerprintSDK from '@cx-sdk/fp';
import RootErrorPage, { DEFAULT_RESOURCES_URL, extractException } from '@cx-web/components/composites/RootErrorPage';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { BetslipProvider } from '@cx-web/feature-betslip/context';
import { FeatureFlagProvider, featureFlags } from '@cx-web/feature-flags/provider';
import { GeolocationProvider } from '@cx-web/feature-geolocation/context';
import { type ILabelActions, LabelProvider } from '@cx-web/feature-i18n';
import { MyOCAuthProvider } from '@cx-web/feature-myoc';
import { MyOCLogin2018Container } from '@cx-web/feature-myoc-login-2018';
import { AuthModalProvider } from '@cx-web/feature-myoc-login-2018/provider';
import { RepubProvider } from '@cx-web/feature-repub';
import { SiteConfigProvider } from '@cx-web/feature-site-config';
import { type MyOCConfig } from '@cx-web/server-utils/types';
import { getRepubFromURL, isErrorWithStatus, updateI18nSingleton } from '@cx-web/utils';
import { captureException } from '@sentry/react-router';
import { createInstance } from 'i18next';

import { type Repub } from '@cx-modules/constants';

import { type loader as rootLoader } from '~/root-loader';

import Body from './body';
import Head from './head';
import { type loader } from './loader.server';

// Re-export loader directly from loader.server, meta and handle from implementation
export { loader } from './loader.server';
export { meta, handle } from './route-impl';

/**
 * Shared root error boundary. It must never fail: it reads only the lightweight
 * `'root'` loader (repub from the OC-Repub header, resourcesUrl from env) and falls
 * back to the URL-derived repub and the CDN default when that data is absent — which
 * is exactly when an API caused the error. RootErrorPage renders the whole document
 * from those inputs alone.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  captureException(extractException(error));

  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  const location = useLocation();
  const repub = rootLoaderData?.repub ?? getRepubFromURL(new URL(`https://www.oddschecker.com${location.pathname}`));
  const errorStatus = isErrorWithStatus(error) ? error.status : null;

  return (
    <RootErrorPage
      errorStatus={errorStatus}
      repub={repub}
      resourcesUrl={rootLoaderData?.resourcesUrl ?? DEFAULT_RESOURCES_URL}
    />
  );
}

const i18n = createInstance();
i18n.use(initReactI18next);

/**
 * Supplies the ported auth composite with the host's label actions and repub-specific policy links.
 * Must render inside I18nextProvider + AuthModalProvider, and inside `<body>` (it renders a modal
 * `<div>`, which is invalid as a direct child of `<html>`).
 *
 * The ported widget's labels live in the catalog as bare `AUTH_*` keys (the legacy myoc-login
 * namespace) — NOT `RESPONSIVE_AUTH_*`. The host `LabelService` (`useLabelActions`) force-prepends
 * `RESPONSIVE_`, so it cannot reach them; read them directly via react-i18next's `label` namespace.
 */
function MyOCLogin2018Driver({
  myocConfig,
  repub,
  resourcesUrl,
}: {
  myocConfig: MyOCConfig;
  repub: Repub;
  resourcesUrl: string;
}) {
  const { t } = useTranslation(['label']);
  const labelActions = useMemo<ILabelActions>(
    () => ({ get: (labelName: string) => t(labelName, { defaultValue: '' }) }),
    [t],
  );

  return (
    <MyOCLogin2018Container
      apiHost={myocConfig.apiHost}
      apiKey={myocConfig.apiKey}
      deviceId={myocConfig.deviceID}
      labelActions={labelActions}
      repub={repub}
      resourcesUrl={resourcesUrl}
    />
  );
}

function PageTemplate() {
  const rootLoaderData = useLoaderData<typeof loader>();
  const appRootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  const resourcesUrl = appRootLoaderData?.resourcesUrl ?? DEFAULT_RESOURCES_URL;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    ENV,
    featureToggleDefinitions,
    geolocationInfo,
    labels,
    lng,
    myocConfig,
    myocData,
    oddsFormat,
    persistedBetslipData,
    repub,
    resources,
    siteConfig,
  } = rootLoaderData;

  updateI18nSingleton(i18n, lng, resources);

  useEffect(() => {
    const fingerprintingEnabled = featureToggleDefinitions.find(
      def => def.name === featureFlags.SUBS__FINGERPRINTING,
    )?.enabled;
    if (!fingerprintingEnabled) {
      return;
    }

    async function fingerprintClient() {
      if (localStorage && !localStorage.getItem('fpsdk-set')) {
        try {
          const fingerprintSDK = await FingerprintSDK.load();
          const result = await fingerprintSDK.get();
          const response = await fetch('/api/cx/fp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(result),
          });
          const payload = (await response.json()) as { visitorId: string };
          if (payload.visitorId) {
            localStorage.setItem('fpsdk-set', payload.visitorId);
          }
        } catch (error) {}
      }
    }
    fingerprintClient();
  }, [featureToggleDefinitions]);

  return (
    <I18nextProvider i18n={i18n}>
      <html lang={lng}>
        <FeatureFlagProvider featureToggleDefinitions={featureToggleDefinitions}>
          <RepubProvider repub={repub}>
            <MyOCAuthProvider myocConfig={myocConfig} myocData={myocData as MyOCData}>
              <GeolocationProvider countryCode={geolocationInfo.countryCode} state={geolocationInfo.subdivisionCode}>
                <SiteConfigProvider siteConfig={siteConfig}>
                  <LabelProvider devEnv={ENV.NODE_ENV !== 'production'} labels={labels}>
                    <BetslipProvider
                      bookmakers={siteConfig.bookmakers}
                      countryCode={geolocationInfo.countryCode}
                      persistedBetslipData={persistedBetslipData}
                      repub={repub}
                      subdivisionCode={geolocationInfo.subdivisionCode}
                      userOddsFormat={oddsFormat}
                    >
                      {/* Framework-agnostic auth-modal seam (NGF-6331). MyOCLogin2018Container renders the
                          ported MyOCLogin2018 composite from the store, replacing the remote myoc-login
                          bundle + legacy bridge (NGF-6371). */}
                      <AuthModalProvider>
                        <Head />
                        <Body>
                          {/* Must render inside <body>: the modal is a <div>, invalid under <html>. */}
                          <MyOCLogin2018Driver myocConfig={myocConfig} repub={repub} resourcesUrl={resourcesUrl} />
                        </Body>
                      </AuthModalProvider>
                    </BetslipProvider>
                  </LabelProvider>
                </SiteConfigProvider>
              </GeolocationProvider>
            </MyOCAuthProvider>
          </RepubProvider>
        </FeatureFlagProvider>
      </html>
    </I18nextProvider>
  );
}

export default PageTemplate;
